import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getTaskService } from '@src/container';
import type { TaskDTO } from '@src/application/tasks/dto';

/**
 * Hook: useGetTaskById
 * Fetches a specific task by ID using React Query
 * 
 * @param taskId - The unique identifier of the task
 * @param options - React Query options
 * @returns Query result with task data
 * 
 * @example
 * ```typescript
 * const { data: task, isLoading, error } = useGetTaskById('task-123');
 * ```
 */
export function useGetTaskById(
    taskId: string,
    options?: Omit<UseQueryOptions<TaskDTO, Error>, 'queryKey' | 'queryFn'>
) {
    const taskService = getTaskService();

    return useQuery({
        queryKey: ['tasks', taskId],
        queryFn: () => taskService.getTaskById.execute(taskId),
        staleTime: 1 * 60 * 1000, // 1 minute
        enabled: Boolean(taskId),
        ...options,
    });
}
